import { useCallback, useEffect, useMemo, useState } from 'react'
import axios from 'axios'
import {
  deleteAdminAccessory,
  formatAxiosAdminError,
  getAdminAccessories,
  patchAdminAccessory,
  postAdminAccessory,
  resolveBackendMediaUrl,
  type AdminAccessoryPatch,
  type AdminAccessoryRow,
} from '@/api'
import { AdminGlassDisclosure } from '@/components/admin/AdminGlassDisclosure'
import { AccessoryImageUploader } from '@/components/AccessoryImageUploader'
import { Loader2 } from 'lucide-react'
import { useHaptic } from '@/hooks/useHaptic'
import { compareAccessoryVitrineSections, ACCESSORY_VITRINE_SECTION_ORDER } from '@/accessorySectionOrder'
import { AccessoryVitrineCardAdmin } from '@/pages/admin/AccessoryVitrineCardAdmin'
import { MotorHandbookMediaAdmin } from '@/pages/admin/MotorHandbookMediaAdmin'

function mergeRow(row: AdminAccessoryRow, draft: Partial<AdminAccessoryRow>): AdminAccessoryRow {
  return {
    ...row,
    ...draft,
    price: draft.price !== undefined ? draft.price : row.price,
  }
}

type Props = {
  catalogWrite: boolean
  token: string | null
}

export function AccessoriesAdminPanel({ catalogWrite, token }: Props) {
  const haptic = useHaptic()
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [rows, setRows] = useState<AdminAccessoryRow[]>([])
  const [drafts, setDrafts] = useState<Record<number, Partial<AdminAccessoryRow>>>({})
  const [savingId, setSavingId] = useState<number | null>(null)
  const [newName, setNewName] = useState('')
  const [newPrice, setNewPrice] = useState('')
  const [newSection, setNewSection] = useState('')
  const [creating, setCreating] = useState(false)
  const load = useCallback(async () => {
    if (!token) return
    setLoading(true)
    setError(null)
    try {
      const data = await getAdminAccessories()
      setRows(Array.isArray(data) ? data : [])
      setDrafts({})
    } catch (e) {
      setRows([])
      setError(formatAxiosAdminError(e, 'Не удалось загрузить аксессуары.'))
    } finally {
      setLoading(false)
    }
  }, [token])

  useEffect(() => {
    void load()
  }, [load])

  const listOnlyRows = useMemo(() => rows.filter((a) => !a.vitrine_group_id), [rows])

  const grouped = useMemo(() => {
    const m = new Map<string, AdminAccessoryRow[]>()
    for (const a of listOnlyRows) {
      const sec = (a.category || '').trim() || 'Без секции'
      if (!m.has(sec)) m.set(sec, [])
      m.get(sec)!.push(a)
    }
    for (const [, list] of m) {
      list.sort((x, y) => (x.sort_order - y.sort_order) || x.name.localeCompare(y.name, 'ru'))
    }
    return [...m.entries()].sort(([a], [b]) => compareAccessoryVitrineSections(a, b))
  }, [listOnlyRows])

  const updateDraft = (id: number, patch: Partial<AdminAccessoryRow>) => {
    setDrafts((prev) => ({ ...prev, [id]: { ...prev[id], ...patch } }))
  }

  const effective = (row: AdminAccessoryRow): AdminAccessoryRow => mergeRow(row, drafts[row.id] ?? {})

  const save = async (id: number) => {
    const d = drafts[id]
    if (!d || !catalogWrite) return
    setSavingId(id)
    try {
      const next = await patchAdminAccessory(id, d as AdminAccessoryPatch)
      setRows((prev) => prev.map((x) => (x.id === id ? next : x)))
      setDrafts((prev) => {
        const x = { ...prev }
        delete x[id]
        return x
      })
      haptic.selection()
    } catch (e) {
      if (axios.isAxiosError(e)) {
        alert(formatAxiosAdminError(e, 'Не удалось сохранить.'))
      }
    } finally {
      setSavingId(null)
    }
  }

  const onImageUploaded = async (id: number, url: string) => {
    if (!catalogWrite) return
    try {
      const next = await patchAdminAccessory(id, { image_url: url })
      setRows((prev) => prev.map((x) => (x.id === id ? next : x)))
      setDrafts((prev) => {
        const x = { ...prev }
        delete x[id]
        return x
      })
      haptic.selection()
    } catch {
      updateDraft(id, { image_url: url })
    }
  }

  const hasDraft = (id: number) => Object.keys(drafts[id] ?? {}).length > 0

  return (
    <div className="space-y-4">
      <AccessoryVitrineCardAdmin catalogWrite={catalogWrite} token={token} onChanged={() => void load()} />
      <MotorHandbookMediaAdmin catalogWrite={catalogWrite} token={token} />
      <AdminGlassDisclosure
        accent="cyan"
        defaultOpen={listOnlyRows.length > 0}
        density="compact"
        title="Позиции списка (без плитки)"
        subtitle="Хабы, датчики, TUYA — строки внизу витрины, не карточки с цветами"
        badge={`${listOnlyRows.length}`}
      >
        {catalogWrite && (
          <div
            className="rounded-xl p-3 mb-3 space-y-2"
            style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(99,179,237,0.2)' }}
          >
            <p className="text-[10px] font-bold uppercase" style={{ color: 'var(--text-3)' }}>
              Новая позиция
            </p>
            <input
              className="input-glass w-full px-2 py-2 rounded-lg text-xs"
              placeholder="Название"
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
            />
            <input
              className="input-glass w-full px-2 py-2 rounded-lg text-xs"
              placeholder="Секция (как на витрине)"
              value={newSection}
              onChange={(e) => setNewSection(e.target.value)}
            />
            <input
              type="number"
              className="input-glass w-full px-2 py-2 rounded-lg text-xs"
              placeholder="Цена (число)"
              value={newPrice}
              onChange={(e) => setNewPrice(e.target.value)}
            />
            <button
              type="button"
              disabled={creating || !newName.trim()}
              className="press w-full py-2 rounded-xl text-[11px] font-bold disabled:opacity-40"
              style={{
                background: 'rgba(72,187,120,0.22)',
                color: '#68D391',
                border: '1px solid rgba(72,187,120,0.4)',
              }}
              onClick={async () => {
                const name = newName.trim()
                if (!name || creating) return
                setCreating(true)
                try {
                  let price: number | null = null;
                  if (newPrice.trim() !== '') {
                    const n = Number(newPrice);
                    price = Number.isFinite(n) ? Math.max(0, n) : null;
                  }
                  await postAdminAccessory({
                    name,
                    price,
                    category: newSection.trim() || null,
                    is_active: true,
                    sort_order: 999,
                  })
                  setNewName('')
                  setNewSection('')
                  setNewPrice('')
                  haptic.selection()
                  await load()
                } catch (e) {
                  if (axios.isAxiosError(e)) alert(formatAxiosAdminError(e, 'Не удалось добавить позицию.'))
                } finally {
                  setCreating(false)
                }
              }}
            >
              {creating ? 'Создание…' : 'Добавить аксессуар'}
            </button>
          </div>
        )}

        <p className="text-[11px] leading-relaxed mb-3" style={{ color: 'var(--text-2)' }}>
          Пульты с цветами настраиваются в блоке <strong>«Карточки пультов»</strong> выше. Здесь — только строки списка.
          Секции: {ACCESSORY_VITRINE_SECTION_ORDER.slice(0, 4).join(', ')}…
        </p>
        <p className="text-[10px] leading-relaxed mb-3 opacity-90" style={{ color: 'var(--text-3)' }}>
          Если здесь и во вкладке каталога «Пульты» одновременно «нет ответа», а «Товары» грузятся — на backend проверьте деплой и{' '}
          <code className="text-[9px] px-1 rounded" style={{ background: 'rgba(255,255,255,0.06)' }}>
            alembic upgrade head
          </code>{' '}
          (нужны колонки accessories: sort_order, category, sku).
        </p>

        {error && (
          <div
            className="rounded-xl px-3 py-2 text-xs mb-3"
            style={{ background: 'rgba(252,129,74,0.12)', color: '#FC814A', border: '1px solid rgba(252,129,74,0.35)' }}
          >
            {error}
            <button type="button" className="underline ml-2 press" onClick={() => void load()}>
              Обновить
            </button>
          </div>
        )}

        {loading && (
          <div className="flex justify-center py-10">
            <Loader2 className="animate-spin" size={24} style={{ color: 'var(--cyan)' }} />
          </div>
        )}

        {!loading &&
          grouped.map(([section, items]) => (
            <div key={section} className="mb-4 last:mb-0">
              <p
                className="text-[10px] font-bold uppercase tracking-wide mb-2 px-1"
                style={{ color: 'var(--text-3)' }}
              >
                {section}
              </p>
              <div className="space-y-2">
                {items.map((raw) => {
                  const r = effective(raw)
                  const img = resolveBackendMediaUrl(r.image_url ?? '')
                  const changed = hasDraft(raw.id)
                  return (
                    <AdminGlassDisclosure
                      key={raw.id}
                      accent="slate"
                      defaultOpen={false}
                      density="compact"
                      title={r.name}
                      subtitle={`#${raw.id}${r.sku ? ` · ${r.sku}` : ''}${r.price != null ? ` · ${r.price} ₽` : ''} · ${r.is_active ? '● в продаже' : '○ скрыт'}`}
                      headerRight={
                        img ? (
                          <img
                            src={img}
                            alt={r.name}
                            className="w-10 h-10 rounded-lg object-cover shrink-0 border border-white/10"
                          />
                        ) : undefined
                      }
                    >
                      <div className="space-y-2 pt-1">
                        <div className="grid grid-cols-2 gap-2">
                          <label className="text-[10px] font-bold" style={{ color: 'var(--text-3)' }}>
                            Название
                            <input
                              className="input-glass w-full mt-0.5 px-2 py-2 rounded-lg text-xs"
                              disabled={!catalogWrite}
                              value={r.name}
                              onChange={(e) => updateDraft(raw.id, { name: e.target.value })}
                            />
                          </label>
                          <label className="text-[10px] font-bold" style={{ color: 'var(--text-3)' }}>
                            SKU / артикул
                            <input
                              className="input-glass w-full mt-0.5 px-2 py-2 rounded-lg text-xs"
                              disabled={!catalogWrite}
                              value={r.sku ?? ''}
                              onChange={(e) => updateDraft(raw.id, { sku: e.target.value.trim() || null })}
                            />
                          </label>
                          <label className="text-[10px] font-bold" style={{ color: 'var(--text-3)' }}>
                            Цена (число)
                            <input
                              type="number"
                              className="input-glass w-full mt-0.5 px-2 py-2 rounded-lg text-xs"
                              disabled={!catalogWrite}
                              value={r.price ?? ''}
                              onChange={(e) => {
                                const v = e.target.value
                                updateDraft(raw.id, {
                                  price: v === '' ? null : Math.max(0, Number(v) || 0),
                                })
                              }}
                            />
                          </label>
                          <label className="text-[10px] font-bold" style={{ color: 'var(--text-3)' }}>
                            Порядок сорт.
                            <input
                              type="number"
                              className="input-glass w-full mt-0.5 px-2 py-2 rounded-lg text-xs"
                              disabled={!catalogWrite}
                              value={r.sort_order}
                              onChange={(e) =>
                                updateDraft(raw.id, { sort_order: Math.trunc(Number(e.target.value) || 0) })
                              }
                            />
                          </label>
                        </div>
                        <label className="block text-[10px] font-bold" style={{ color: 'var(--text-3)' }}>
                          Секция на витрине
                          <input
                            className="input-glass w-full mt-0.5 px-2 py-2 rounded-lg text-xs"
                            disabled={!catalogWrite}
                            value={r.category ?? ''}
                            placeholder="Аксессуары и контроллеры"
                            onChange={(e) => updateDraft(raw.id, { category: e.target.value.trim() || null })}
                          />
                        </label>
                        <label className="block text-[10px] font-bold" style={{ color: 'var(--text-3)' }}>
                          Описание для витрины
                          <textarea
                            rows={3}
                            className="input-glass w-full mt-0.5 px-2 py-2 rounded-lg text-xs"
                            disabled={!catalogWrite}
                            value={r.description ?? ''}
                            onChange={(e) => updateDraft(raw.id, { description: e.target.value })}
                          />
                        </label>

                        <label className="flex items-center gap-2 text-[11px]" style={{ color: 'var(--text-2)' }}>
                          <input
                            type="checkbox"
                            disabled={!catalogWrite}
                            checked={r.is_active}
                            onChange={(e) => updateDraft(raw.id, { is_active: e.target.checked })}
                          />
                          Активна на витрине
                        </label>
                        {catalogWrite && (
                          <div>
                            <p className="text-[10px] font-bold mb-1" style={{ color: 'var(--text-3)' }}>
                              Обложка (витрина)
                            </p>
                            <AccessoryImageUploader
                              accessoryId={raw.id}
                              currentImageUrl={img}
                              disabled={!catalogWrite}
                              onUpload={(url) => void onImageUploaded(raw.id, url)}
                            />
                          </div>
                        )}

                        {catalogWrite && (
                          <button
                            type="button"
                            disabled={!changed || savingId === raw.id}
                            className="press w-full py-2 rounded-xl text-xs font-bold disabled:opacity-40"
                            style={{
                              background: 'rgba(72,187,120,0.2)',
                              color: '#68D391',
                              border: '1px solid rgba(72,187,120,0.4)',
                            }}
                            onClick={() => void save(raw.id)}
                          >
                            {savingId === raw.id ? 'Сохраняем…' : changed ? 'Сохранить позицию' : 'Нет изменений'}
                          </button>
                        )}
                        {catalogWrite && (
                          <button
                            type="button"
                            className="press w-full py-2 rounded-xl text-[11px] font-bold mt-2"
                            style={{
                              background: 'rgba(252,129,74,0.12)',
                              color: '#FC814A',
                              border: '1px solid rgba(252,129,74,0.35)',
                            }}
                            onClick={async () => {
                              if (!window.confirm(`Удалить позицию «${r.name}» (#${raw.id})?`)) return
                              try {
                                await deleteAdminAccessory(raw.id)
                                haptic.selection()
                                await load()
                              } catch (e) {
                                if (axios.isAxiosError(e)) alert(formatAxiosAdminError(e, 'Удалить не удалось.'))
                              }
                            }}
                          >
                            Удалить из базы
                          </button>
                        )}
                      </div>
                    </AdminGlassDisclosure>
                  )
                })}
              </div>
            </div>
          ))}
      </AdminGlassDisclosure>
    </div>
  )
}
